import { For, Show, createSignal } from "solid-js";

import { useProduct, useProducts } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  value: () => string | null;
  // Receives the product id and the picked product's display name + unit_price
  // so callers (the line-item form) can snapshot the price into their form
  // state without a second request.
  onChange: (
    productId: string | null,
    name: string | null,
    unitPrice: number | null,
    currency: string | null,
  ) => void;
  selectedName?: () => string | null;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
};

export function ProductPicker(props: Props) {
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);

  const products = useProducts(props.wsKey, () => ({
    q: query() || undefined,
    active: true,
    limit: 12,
  }));

  // Auto-resolve the selected product's name when the parent didn't pre-supply
  // one (same pattern as AccountPicker / ContactPicker / UserPicker).
  const resolved = useProduct(props.wsKey, () => props.value());

  const chipLabel = () => {
    const supplied = props.selectedName?.();
    if (supplied) return supplied;
    if (resolved.data) return resolved.data.name;
    return props.value()?.slice(0, 14) ?? "Product";
  };

  const size = () => props.size ?? "md";
  const inputCls = () =>
    size() === "sm" ? "input input-bordered input-sm w-full" : "input input-bordered w-full";

  const clear = () => {
    props.onChange(null, null, null, null);
    setQuery("");
  };

  const pick = (id: string, name: string, price: number | null, currency: string | null) => {
    props.onChange(id, name, price, currency);
    setQuery("");
    setOpen(false);
  };

  return (
    <div class="relative">
      <Show
        when={props.value()}
        fallback={
          <div>
            <input
              type="text"
              class={inputCls()}
              placeholder={props.placeholder ?? "Search products…"}
              aria-label={props.label ?? "Search products"}
              value={query()}
              disabled={props.disabled}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setOpen(false), 150);
              }}
              autocomplete="off"
            />
            <Show when={open() && query().length > 0}>
              <ul
                role="listbox"
                aria-label="Product matches"
                class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
              >
                <For each={products.data?.items ?? []}>
                  {(p) => (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(p.id, p.name, p.unit_price, p.currency)}
                      >
                        <div class="font-medium">{p.name}</div>
                        <div class="text-xs opacity-60 flex gap-2">
                          <Show when={p.sku}>
                            <span>
                              <code>{p.sku}</code>
                            </span>
                          </Show>
                          <Show when={p.unit_price !== null}>
                            <span>
                              {p.unit_price?.toFixed(2)} {p.currency ?? ""}
                            </span>
                          </Show>
                        </div>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={(products.data?.items.length ?? 0) === 0 && !products.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">No products match.</li>
                </Show>
                <Show when={products.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">Searching…</li>
                </Show>
              </ul>
            </Show>
          </div>
        }
      >
        <div
          class={`badge ${
            size() === "sm" ? "badge-md" : "badge-lg"
          } badge-primary badge-outline gap-2`}
        >
          <span class="truncate max-w-xs">{chipLabel()}</span>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle"
            aria-label="Clear product"
            disabled={props.disabled}
            onClick={clear}
          >
            ✕
          </button>
        </div>
      </Show>
    </div>
  );
}
